/**
 * AYU Mikrobiom-Analyse
 *
 * Zeigt die vom Team erstellte Mikrobiom-Auswertung an.
 * Kein Client-Upload — Berichte werden ausschließlich über das Admin-Center bereitgestellt.
 *
 * NUR für DNA-Paket-Nutzer (premium) verfügbar.
 * Basis-Nutzer sehen einen gesperrten Zustand mit Upgrade-Hinweis.
 */
import { useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import {
  Microscope,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Lock,
  Dna,
  ArrowUpRight,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { motion, AnimatePresence } from "framer-motion";
import { useClientData } from "@/hooks/useClientData";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import {
  PHENOTYPES,
  type PhenotypeId,
} from "../../../../shared/phenotypeScoring";
import { toast } from "sonner";
import PromoCodeInput from "@/components/PromoCodeInput";
import {
  Activity,
  BarChart3,
  Brain,
  MessageCircle,
  CalendarClock,
  Clock,
} from "lucide-react";
import { useLocation } from "wouter";





/* ─── Upgrade Button (Basis → DNA) ─── */
function UpgradeButton() {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const upgradeMutation = trpc.stripe.createUpgradeCheckout.useMutation({
    onSuccess: data => {
      if (data.url) {
        toast.info(t("mikrobiom.upgradeRedirecting"));
        window.location.href = data.url;
      }
    },
    onError: err => {
      toast.error(err.message || t("mikrobiom.upgradeFehler"));
      setIsLoading(false);
    },
  });

  const handleUpgrade = () => {
    setIsLoading(true);
    upgradeMutation.mutate({ origin: window.location.origin });
  };

  return (
    <button
      onClick={handleUpgrade}
      disabled={isLoading || upgradeMutation.isPending}
      className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#d3ab5f] text-white text-sm font-medium hover:shadow-lg hover:shadow-[#8b6914]/20 transition-all duration-200 disabled:opacity-50"
    >
      {isLoading || upgradeMutation.isPending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Dna className="w-4 h-4" />
      )}
      <span>{t("mikrobiom.upgradeBtn")}</span>
      <ArrowUpRight className="w-4 h-4" />
    </button>
  );
}

/* ─── Locked State für Basis-Nutzer ─── */
function MicrobiomeLockedView() {
  const { t } = useI18n();
  return (
    <div className="max-w-3xl mx-auto py-6 px-4 space-y-8">
      {/* Header — grau */}
      <div className="opacity-50">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
          <Microscope className="w-7 h-7 text-gray-400" />
          {t("mikrobiom.title")}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t("mikrobiom.subtitle")}
        </p>
      </div>

      {/* Lock-Overlay-Karte */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-2 border-dashed border-[#d4c9b8] rounded-2xl p-8 text-center space-y-6"
      >
        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
          <Lock className="w-10 h-10 text-gray-400" />
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-[#2d3a1e]">
            {t("mikrobiom.nurDNA")}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            {t("mikrobiom.nurDNADesc")}
          </p>
        </div>

        <div className="bg-[#f8f8f8] rounded-xl p-5 max-w-sm mx-auto space-y-3">
          <p className="text-xs font-medium text-[#8b6914] uppercase tracking-wider">
            {t("mikrobiom.dasErwartet")}
          </p>
          <ul className="text-sm text-[#5a6b4a] space-y-2 text-left">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#6b7f4e] mt-0.5 shrink-0" />
              <span>{t("mikrobiom.lockedBullet1")}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#6b7f4e] mt-0.5 shrink-0" />
              <span>{t("mikrobiom.lockedBullet2")}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#6b7f4e] mt-0.5 shrink-0" />
              <span>{t("mikrobiom.lockedBullet3")}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#6b7f4e] mt-0.5 shrink-0" />
              <span>{t("mikrobiom.lockedBullet4")}</span>
            </li>
          </ul>
        </div>

        <UpgradeButton />

        <p className="text-xs text-muted-foreground">
          {t("mikrobiom.upgradePreis")}
        </p>

        <PromoCodeInput />
      </motion.div>

      {/* Grau hinterlegte Vorschau der Funktionen */}
      <div className="opacity-30 pointer-events-none select-none space-y-6">
        {/* Vorschau: Analyse-Karte */}
        <div className="bg-white border border-[#d4c9b8] rounded-xl p-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Microscope className="w-5 h-5 text-gray-300" />
            </div>
            <div>
              <p className="text-base font-medium text-gray-400">{t("mikrobiom.deineAuswertung")}</p>
              <p className="text-xs text-gray-300">{t("mikrobiom.diversitaet", { n: "--" })}</p>
            </div>
          </div>
          <div className="h-3 bg-gray-100 rounded-full w-3/4"></div>
          <div className="h-3 bg-gray-100 rounded-full w-1/2"></div>
        </div>
      </div>
    </div>
  );
}

export default function AppMicrobiome() {
  const { t, locale } = useI18n();
  const clientData = useClientData();
  const { client } = useClientAuth();
  const clientId = clientData.clientId || "";
  const [showDetails, setShowDetails] = useState(false);
  const [refineComplete, setRefineComplete] = useState(false);

  // Basis-Nutzer sehen gesperrten Zustand
  const isBasis = client?.packageType !== "premium";
  if (isBasis) {
    return <MicrobiomeLockedView />;
  }

  // Fetch Gesamtanalyse (admin-created)
  const gesamtanalyseQuery = trpc.microbiome.getGesamtanalyse.useQuery(
    { clientId },
    { enabled: !!clientId }
  );

  // Fetch latest microbiome report (admin-uploaded)
  const latestQuery = trpc.microbiome.getLatest.useQuery(
    { clientId },
    { enabled: !!clientId }
  );

  const refineMutation = trpc.microbiome.refine.useMutation({
    onSuccess: data => {
      if (data.success) {
        setRefineComplete(true);
        const oldName =
          PHENOTYPES[data.previousPrimary as PhenotypeId]?.name ||
          data.previousPrimary;
        const newName =
          PHENOTYPES[data.newPrimary as PhenotypeId]?.name || data.newPrimary;
        if (data.changed) {
          toast.success(t("mikrobiom.phenotypVerfeinertToast", { from: oldName, to: newName }));
        } else {
          toast.success(t("mikrobiom.phenotypBestaetigt", { name: newName }));
        }
      }
    },
    onError: err => {
      toast.error(err.message || t("mikrobiom.verfeinernFehler"));
    },
  });

  const handleRefine = () => {
    if (!latestQuery.data?.docId) return;
    refineMutation.mutate({
      clientId,
      microbiomeDocId: latestQuery.data.docId,
    });
  };

  // Use analysis from latest admin-uploaded report
  const displayAnalysis = latestQuery.data?.analysis;

  const dateLocale = locale === "en" ? "en-GB" : "de-DE";

  return (
    <div className="max-w-3xl mx-auto py-6 px-4 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
          <Microscope className="w-7 h-7 text-[#6b7f4e]" />
          {t("mikrobiom.title")}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t("mikrobiom.subtitle")}
        </p>
      </div>

      {/* Empfehlung: Nächstes Mikrobiom */}
      {(gesamtanalyseQuery.data?.reportDate || gesamtanalyseQuery.data?.createdAt) && (() => {
        const reportDate = gesamtanalyseQuery.data.reportDate
          ? new Date(gesamtanalyseQuery.data.reportDate)
          : new Date(gesamtanalyseQuery.data.createdAt);
        const analysisDate = reportDate;
        const nextDate = new Date(analysisDate);
        nextDate.setMonth(nextDate.getMonth() + 6);
        const isOverdue = nextDate < new Date();
        const formattedDate = nextDate.toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" });
        return (
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
            isOverdue
              ? "bg-amber-50 border-amber-200"
              : "bg-[#f0ebe2] border-[#d4c9b8]"
          }`}>
            <CalendarClock className={`w-5 h-5 shrink-0 ${
              isOverdue ? "text-amber-600" : "text-[#6b7f4e]"
            }`} />
            <div>
              <p className={`text-sm font-medium ${
                isOverdue ? "text-amber-800" : "text-foreground"
              }`}>
                {isOverdue
                  ? t("mikrobiom.mikrobiomFaellig")
                  : t("mikrobiom.naechstesMikrobiom")}
              </p>
              <p className={`text-xs ${
                isOverdue ? "text-amber-600" : "text-muted-foreground"
              }`}>
                {isOverdue
                  ? t("mikrobiom.seitEmpfohlen", { date: formattedDate })
                  : t("mikrobiom.abEmpfohlen", { date: formattedDate })}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Gesamtanalyse (admin-created) */}
      {gesamtanalyseQuery.data && (
        <GesamtanalyseClientView data={gesamtanalyseQuery.data} />
      )}

      {/* Info: Warten auf Laborergebnisse */}
      {!gesamtanalyseQuery.data && !displayAnalysis && (
        <div className="bg-[#f0ebe2] border border-[#d4c9b8] rounded-xl p-6 text-center space-y-3">
          <div className="w-14 h-14 rounded-full bg-[#6b7f4e]/10 flex items-center justify-center mx-auto">
            <Clock className="w-7 h-7 text-[#6b7f4e]" />
          </div>
          <div>
            <p className="text-base font-medium text-foreground">
              {t("mikrobiom.wartenLaborergebnisse")}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {t("mikrobiom.wartenDesc")}
            </p>
          </div>
        </div>
      )}

      {/* Results from admin-uploaded report */}
      {displayAnalysis && !gesamtanalyseQuery.data && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Summary Card */}
          <div className="bg-white border border-[#d4c9b8] rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t("mikrobiom.analyseAbgeschlossen")}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("mikrobiom.anbieter", { value: displayAnalysis.provider || t("mikrobiom.unbekannt") })}
                </p>
              </div>
            </div>

            <p className="text-sm text-foreground/80 leading-relaxed">
              {displayAnalysis.summary}
            </p>

            {/* Recommendations */}
            {displayAnalysis.recommendations && (
              <div className="bg-[#f0ebe2] rounded-lg p-4">
                <p className="text-xs font-medium text-[#6b7f4e] mb-1">
                  {t("mikrobiom.empfehlungen")}
                </p>
                <p className="text-sm text-[#5a6b4a] leading-relaxed">
                  {displayAnalysis.recommendations}
                </p>
              </div>
            )}
          </div>

          {/* Extracted Data */}
          {displayAnalysis.extractedData && (
            <div className="bg-white border border-[#d4c9b8] rounded-xl overflow-hidden">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <span className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Microscope className="w-4 h-4 text-[#6b7f4e]" />
                  {t("mikrobiom.extrahierteDaten")}
                </span>
                {showDetails ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-[#d4c9b8]"
                  >
                    <div className="p-4 space-y-3">
                      {displayAnalysis.extractedData.diversityIndex && (
                        <DataRow
                          label={t("mikrobiom.diversitaetsindex")}
                          value={displayAnalysis.extractedData.diversityIndex}
                        />
                      )}
                      {displayAnalysis.extractedData.fbRatio && (
                        <DataRow
                          label="F/B-Ratio"
                          value={displayAnalysis.extractedData.fbRatio}
                        />
                      )}
                      {displayAnalysis.extractedData.keyBacteria && (
                        <DataRow
                          label={t("mikrobiom.schluesselBakterien")}
                          value={displayAnalysis.extractedData.keyBacteria}
                        />
                      )}
                      {displayAnalysis.extractedData.inflammationMarkers && (
                        <DataRow
                          label={t("mikrobiom.entzuendungsmarker")}
                          value={
                            displayAnalysis.extractedData.inflammationMarkers
                          }
                        />
                      )}
                      {displayAnalysis.extractedData.scfa && (
                        <DataRow
                          label={t("mikrobiom.kurzkettFettsaeuren")}
                          value={displayAnalysis.extractedData.scfa}
                        />
                      )}
                      {displayAnalysis.extractedData.abnormalities && (
                        <DataRow
                          label={t("mikrobiom.auffaelligkeiten")}
                          value={displayAnalysis.extractedData.abnormalities}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Phenotype Scores */}
          {displayAnalysis.phenotypeScores && (
            <div className="bg-white border border-[#d4c9b8] rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#c9a84c]" />
                {t("mikrobiom.phenotypRelevanzMikrobiom")}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(
                  displayAnalysis.phenotypeScores as Record<string, number>
                )
                  .sort(([, a], [, b]) => b - a)
                  .filter(([, score]) => score > 0)
                  .map(([id, score]) => {
                    const phenotype = PHENOTYPES[id as PhenotypeId];
                    if (!phenotype) return null;
                    const maxScore = Math.max(
                      ...Object.values(
                        displayAnalysis.phenotypeScores as Record<
                          string,
                          number
                        >
                      )
                    );
                    const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;

                    return (
                      <div key={id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs text-foreground truncate">
                              {phenotype.name}
                            </span>
                            <span className="text-xs text-muted-foreground ml-1">
                              {score}
                            </span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#6b7f4e] rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Refine Phenotype Button */}
          {!refineComplete ? (
            <motion.button
              onClick={handleRefine}
              disabled={refineMutation.isPending}
              className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-gradient-to-r from-[#6b7f4e] to-[#5a6b4a] text-white rounded-xl font-medium text-base hover:shadow-lg transition-all disabled:opacity-60"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {refineMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t("mikrobiom.phenotypWirdVerfeinert")}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {t("mikrobiom.phenotypVerfeinern")}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center space-y-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
              <p className="text-sm font-medium text-emerald-800">
                {t("mikrobiom.phenotypVerfeinertErfolgreich")}
              </p>
              <p className="text-xs text-emerald-600">
                {t("mikrobiom.phenotypVerfeinertDesc")}
              </p>
            </div>
          )}


        </motion.div>
      )}
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs font-medium text-muted-foreground w-36 shrink-0">
        {label}
      </span>
      <span className="text-xs text-foreground leading-relaxed">{value}</span>
    </div>
  );
}

/* ─── Gesamtanalyse Client View ─── */
/* ─── Coach-Button Komponente ─── */
function CoachDiscussButton({
  label,
  sectionContext,
}: {
  label: string;
  sectionContext: string;
}) {
  const [, setLocation] = useLocation();

  const handleClick = () => {
    // Encode the section context as a URL parameter for the coach
    const params = new URLSearchParams({
      mode: "chat",
      context: `mikrobiom:${sectionContext}`,
    });
    setLocation(`/app/coach?${params.toString()}`);
  };

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
        bg-[#6b7f4e]/10 text-[#6b7f4e] hover:bg-[#6b7f4e]/20 transition-colors"
    >
      <MessageCircle className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function GesamtanalyseClientView({ data }: { data: {
  summary: string | null;
  analysisData: any;
  createdAt: any;
  sourcesUsed: any;
} }) {
  const { t, locale } = useI18n();
  const [showDetails, setShowDetails] = useState(false);
  const ad = data.analysisData as any;
  const dateLocale = locale === "en" ? "en-GB" : "de-DE";

  if (!ad) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Header Card */}
      <div className="bg-white border border-[#d4c9b8] rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#c9a84c]/10 flex items-center justify-center">
            <Brain className="w-5 h-5 text-[#c9a84c]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t("mikrobiom.deineAuswertung")}
            </h2>
            <div className="flex items-center gap-2 mt-0.5">
              {ad.overallStatus && (
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    ad.overallStatus === "gut"
                      ? "bg-emerald-100 text-emerald-700"
                      : ad.overallStatus === "auffaellig"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700"
                  }`}
                >
                  {ad.overallStatus === "gut" ? t("mikrobiom.statusGut") : ad.overallStatus === "auffaellig" ? t("mikrobiom.statusAuffaellig") : t("mikrobiom.statusKritisch")}
                </span>
              )}
              {ad.diversityScore !== null && ad.diversityScore !== undefined && (
                <span className="text-xs text-muted-foreground">
                  {t("mikrobiom.diversitaet", { n: String(ad.diversityScore) })}
                </span>
              )}
            </div>
          </div>
        </div>

        {data.summary && (
          <p className="text-sm text-foreground/80 leading-relaxed">
            {data.summary}
          </p>
        )}

        {/* Button: Ganze Analyse mit Coach besprechen */}
        <CoachDiscussButton
          label={t("mikrobiom.ganzeBesprechen")}
          sectionContext="full"
        />
      </div>

      {/* Key Findings */}
      {ad.keyFindings?.length > 0 && (
        <div className="bg-white border border-[#d4c9b8] rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#6b7f4e]" />
            {t("mikrobiom.wichtigsteBefunde")}
          </h3>
          <div className="space-y-2">
            {ad.keyFindings.map((f: any, i: number) => (
              <div
                key={i}
                className={`flex items-start gap-3 p-3 rounded-lg ${
                  f.severity === "kritisch"
                    ? "bg-red-50"
                    : f.severity === "warnung"
                      ? "bg-amber-50"
                      : "bg-[#f0ebe2]"
                }`}
              >
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                    f.severity === "kritisch"
                      ? "bg-red-100 text-red-700"
                      : f.severity === "warnung"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {f.category}
                </span>
                <span className="text-sm text-foreground/80">{f.finding}</span>
              </div>
            ))}
          </div>
          <CoachDiscussButton
            label={t("mikrobiom.befundeBesprechen")}
            sectionContext="keyFindings"
          />
        </div>
      )}

      {/* Recommendations */}
      {ad.recommendations?.length > 0 && (
        <div className="bg-white border border-[#d4c9b8] rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#6b7f4e]" />
            {t("mikrobiom.empfehlungenFuer")}
          </h3>
          <div className="space-y-2">
            {ad.recommendations.map((r: any, i: number) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#f0ebe2]">
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                    r.priority === "hoch"
                      ? "bg-red-100 text-red-700"
                      : r.priority === "mittel"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-green-100 text-green-700"
                  }`}
                >
                  {r.area}
                </span>
                <span className="text-sm text-foreground/80">{r.recommendation}</span>
              </div>
            ))}
          </div>
          <CoachDiscussButton
            label={t("mikrobiom.empfehlungenBesprechen")}
            sectionContext="recommendations"
          />
        </div>
      )}

      {/* Phenotype Scores */}
      {ad.phenotypeScores && Object.keys(ad.phenotypeScores).length > 0 && (
        <div className="bg-white border border-[#d4c9b8] rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#c9a84c]" />
            {t("mikrobiom.phenotypRelevanz")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(ad.phenotypeScores)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .filter(([, score]) => (score as number) > 0)
              .map(([key, score]) => {
                const phenotype = PHENOTYPES[key as PhenotypeId];
                const maxScore = Math.max(...Object.values(ad.phenotypeScores as Record<string, number>));
                const pct = maxScore > 0 ? ((score as number) / maxScore) * 100 : 0;
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground">{phenotype?.name || key}</span>
                      <span className="text-xs text-muted-foreground">{score as number}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#6b7f4e] rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
          <CoachDiscussButton
            label={t("mikrobiom.phenotypBesprechen")}
            sectionContext={`${t("mikrobiom.phenotypRelevanzMikrobiom")}: ${Object.entries(ad.phenotypeScores as Record<string, number>)
              .sort(([, a], [, b]) => b - a)
              .filter(([, score]) => score > 0)
              .map(([key, score]) => {
                const p = PHENOTYPES[key as PhenotypeId];
                return `${p?.name || key}: ${score}`;
              }).join(", ")}`}
          />
        </div>
      )}

      {/* Detailed Sections */}
      {ad.detailedSections?.length > 0 && (
        <div className="bg-white border border-[#d4c9b8] rounded-xl overflow-hidden">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-foreground flex items-center gap-2">
              <Microscope className="w-4 h-4 text-[#6b7f4e]" />
              {t("mikrobiom.detaillierteAnalyse")}
            </span>
            {showDetails ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-[#d4c9b8]"
              >
                <div className="p-4 space-y-4">
                  {ad.detailedSections.map((s: any, i: number) => (
                    <div key={i}>
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-foreground mb-1">{s.title}</h4>
                        <CoachDiscussButton
                          label={t("mikrobiom.besprechen")}
                          sectionContext={`detail:${i}:${s.title}`}
                        />
                      </div>
                      <p className="text-sm text-foreground/70 leading-relaxed whitespace-pre-wrap">{s.content}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Metadata */}
      <p className="text-xs text-center text-muted-foreground">
        {t("mikrobiom.analyseErstellt", { date: data.createdAt ? new Date(data.createdAt).toLocaleDateString(dateLocale) : "—" })}
        {" · "}{t("mikrobiom.datenquellen", { n: String((data.sourcesUsed as any)?.totalFiles || "?") })}
      </p>
    </motion.div>
  );
}
